import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect, useState, useCallback } from "react";
import {
  ArrowRight,
  Shield,
  Zap,
  Smartphone,
  TrendingUp,
  ChevronDown,
  Check,
  Star,
} from "lucide-react";
import { api } from "@/lib/api";

/* ═══════════════════════════════════════════════════════════════════════════
   ANALYTICS
   ═══════════════════════════════════════════════════════════════════════════ */
const trackEvent = (eventName, data = {}) => {
  if (window.gtag) window.gtag("event", eventName, data);
  if (window.fbq) window.fbq("trackCustom", eventName, data);
  window.dispatchEvent(
    new CustomEvent("smart_home_analytics", {
      detail: { event: eventName, ...data, ts: Date.now() },
    })
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MEDIA — swap assets here without touching components
   ═══════════════════════════════════════════════════════════════════════════ */
const HERO_VIDEO =
  "https://videos.pexels.com/video-files/3120719/3120719-hd_1920_1080_30fps.mp4";
const HERO_POSTER =
  "https://images.unsplash.com/photo-1772112334845-86016056137b?w=1400&q=80";

const SCENARIOS = [
  {
    id: "arrival",
    tag: "01",
    title: "Llegas. Todo está listo.",
    benefit:
      "La puerta se abre sola. La luz te recibe en su punto exacto. La temperatura es la que amas. No tocaste un botón. Tu casa ya sabía que venías.",
    hook: "Como llegar a un hotel de cinco estrellas. Todos los días.",
    image:
      "https://images.unsplash.com/photo-1740842311434-522bb411af15?w=1200&q=80",
    products: [
      {
        name: "August Wi-Fi Smart Lock",
        desc: "Se abre con tu presencia. Sin llaves, sin códigos. Solo llegas.",
      },
      {
        name: "Philips Hue Starter Kit",
        desc: "16 millones de colores. Tu ambiente perfecto cada que entras.",
      },
      {
        name: "Google Nest Thermostat",
        desc: "Aprende tu rutina y ajusta el clima antes de que lo pidas.",
      },
      {
        name: "Aqara Motion Sensor",
        desc: "Detecta tu llegada y activa toda tu escena favorita.",
      },
    ],
  },
  {
    id: "pets",
    tag: "02",
    title: "Siempre cerca de ellos.",
    benefit:
      "Aunque estés en la oficina, los ves, les hablas, les das su comida a la hora exacta. Ellos sienten tu presencia. Porque la familia nunca debería sentirse sola.",
    hook: "Ellos no entienden por qué te fuiste. Pero saben que estás ahí.",
    image:
      "https://images.pexels.com/photos/40986/dog-bulldog-white-tongue-40986.jpeg?auto=compress&cs=tinysrgb&w=1200",
    products: [
      {
        name: "Furbo 360° Camera",
        desc: "Los ves, les hablas y lanzas premios desde tu oficina.",
      },
      {
        name: "Petlibro Granary Feeder",
        desc: "Porciones exactas a la hora exacta. Nunca se quedan sin comer.",
      },
      {
        name: "Tractive GPS Tracker",
        desc: "Sabes dónde están. Siempre. En tiempo real en tu celular.",
      },
      {
        name: "Catit Pixi Smart Fountain",
        desc: "Agua fresca y filtrada todo el día. Para los más consentidos.",
      },
    ],
  },
  {
    id: "security",
    tag: "03",
    title: "Duerme sin pensar en nada.",
    benefit:
      "Tu hogar vigila mientras tú descansas. Detecta lo inusual, te avisa al instante y actúa antes de que tú lo pidas. Tus hijos duermen tranquilos. Tú también.",
    hook: "La tranquilidad de saber que todo está bien. Siempre.",
    image:
      "https://images.unsplash.com/photo-1749930206000-179d0b85aa7e?w=1200&q=80",
    products: [
      {
        name: "Ring Video Doorbell Pro 2",
        desc: "Ves quién toca desde cualquier parte del mundo.",
      },
      {
        name: "Arlo Pro 5S 2K",
        desc: "Visión nocturna en color. Detecta personas, no sombras.",
      },
      {
        name: "Ajax StarterKit Pro",
        desc: "Alarma profesional con sensores de movimiento y apertura.",
      },
      {
        name: "Eve Door & Window",
        desc: "Sabe si una puerta se abrió. Te avisa al instante.",
      },
    ],
  },
  {
    id: "lifestyle",
    tag: "04",
    title: "Cada mañana, sin esfuerzo.",
    benefit:
      "Las cortinas se abren con el sol. Tu café se prepara solo. La música sigue tu humor. Tu rutina fluye como nunca antes.",
    hook: "El verdadero lujo no se ve. Se siente.",
    image:
      "https://images.pexels.com/photos/6269888/pexels-photo-6269888.jpeg?auto=compress&cs=tinysrgb&w=1200",
    products: [
      {
        name: "Sonos Era 300",
        desc: "Audio espacial en cada habitación. Música que te envuelve.",
      },
      {
        name: "IKEA FYRTUR Smart Blinds",
        desc: "Se abren con el amanecer. Se cierran al atardecer. Solas.",
      },
      {
        name: "Roborock S8 MaxV Ultra",
        desc: "Aspira, trapea y se vacía solo. Tu piso siempre perfecto.",
      },
      {
        name: "Amazon Echo Show 15",
        desc: "Centro de control familiar. Calendario, clima, recetas, todo.",
      },
    ],
  },
];

const TRUST_ITEMS = [
  {
    icon: Zap,
    stat: "-30%",
    title: "En tu factura",
    desc: "Tu hogar aprende a gastar solo lo necesario. Confort completo, costo inteligente.",
  },
  {
    icon: Shield,
    stat: "24/7",
    title: "Tu familia, protegida",
    desc: "Monitoreo continuo que cuida lo que más importa. Mientras tú vives tranquilo.",
  },
  {
    icon: Smartphone,
    stat: "1 app",
    title: "Todo en tu mano",
    desc: "Controlas cada rincón desde tu teléfono. En la oficina, de viaje, o en la cama.",
  },
  {
    icon: TrendingUp,
    stat: "+25%",
    title: "Tu patrimonio crece",
    desc: "Una casa inteligente no solo se vive. Se revaloriza con cada año que pasa.",
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION 1 — HERO IMMERSIVE
   ═══════════════════════════════════════════════════════════════════════════ */
function FunnelHero({ onCTAClick }) {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const y = useTransform(scrollYProgress, [0, 0.5], [0, 60]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.08]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          v.play().catch(() => {});
          trackEvent("smart_home_video_play", { section: "hero" });
        } else {
          v.pause();
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(v);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="relative h-[80vh] md:h-[88vh] overflow-hidden"
      data-testid="smart-home-hero"
    >
      <motion.div className="absolute inset-0" style={{ scale }}>
        <img
          src={HERO_POSTER}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1.5s] ${
            videoReady ? "opacity-0" : "opacity-100"
          }`}
        />
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          preload="metadata"
          onCanPlayThrough={() => setVideoReady(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1.5s] ${
            videoReady ? "opacity-100" : "opacity-0"
          }`}
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-black/20" />
      </motion.div>

      <motion.div
        style={{ opacity, y }}
        className="relative z-10 h-full flex flex-col justify-end pb-14 md:pb-24 px-6 md:px-12 lg:px-24 max-w-[1800px] mx-auto"
      >
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="w-8 h-px bg-white/30" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-white/40">
              Hogares Inteligentes — ZYM
            </span>
          </div>

          <h2
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium text-white tracking-tight leading-[0.95] max-w-4xl"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Donde todo sucede
            <br />
            <span className="text-white/35">por ti.</span>
          </h2>

          <p className="mt-6 text-lg md:text-xl text-white/65 font-light max-w-xl leading-relaxed">
            Tu hogar se anticipa a cada momento. Tú solo disfrutas.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-start gap-4">
            <button
              onClick={() => {
                trackEvent("smart_home_cta_click", {
                  location: "hero",
                  type: "primary",
                });
                onCTAClick();
              }}
              className="group inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full text-sm font-medium hover:shadow-[0_10px_50px_rgba(255,255,255,0.2)] transition-all duration-500 active:scale-[0.97]"
              data-testid="smart-home-hero-cta"
            >
              Quiero vivir así
              <ArrowRight
                size={16}
                strokeWidth={2}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
            <button
              onClick={() => {
                trackEvent("smart_home_cta_click", {
                  location: "hero",
                  type: "explore",
                });
                document
                  .querySelector("#smart-home-stories")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center gap-2 text-white/35 hover:text-white/60 text-sm font-light transition-colors duration-300"
              data-testid="smart-home-hero-explore"
            >
              Descubre la experiencia
              <ChevronDown size={14} strokeWidth={1.5} />
            </button>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION 2 — STORYTELLING SCENARIOS
   ═══════════════════════════════════════════════════════════════════════════ */
function ScenarioCard({ scenario, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const reversed = index % 2 !== 0;

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.0, 1.15]);

  useEffect(() => {
    if (inView)
      trackEvent("smart_home_scroll_depth", {
        scenario: scenario.id,
        index,
      });
  }, [inView, scenario.id, index]);

  return (
    <div
      ref={ref}
      className="grid grid-cols-1 lg:grid-cols-2 min-h-[50vh] md:min-h-[55vh]"
      data-testid={`smart-home-scenario-${scenario.id}`}
    >
      {/* Image */}
      <motion.div
        className={`relative overflow-hidden ${
          reversed ? "lg:order-2" : "lg:order-1"
        }`}
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      >
        <motion.img
          src={scenario.image}
          alt={scenario.title}
          loading="lazy"
          style={{ scale: imgScale }}
          className="w-full h-full min-h-[280px] md:min-h-[380px] object-cover"
        />
      </motion.div>

      {/* Text */}
      <div
        className={`flex items-center bg-[#0A0A0A] p-8 md:p-12 lg:p-16 xl:p-24 ${
          reversed ? "lg:order-1" : "lg:order-2"
        }`}
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{
            duration: 0.7,
            delay: 0.15,
            ease: [0.16, 1, 0.3, 1],
          }}
          className="max-w-lg"
        >
          <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-white/35 block mb-4">
            {scenario.tag}
          </span>
          <h3
            className="text-2xl md:text-3xl lg:text-4xl font-medium text-white tracking-tight leading-[1.1] mb-5"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {scenario.title}
          </h3>
          <p className="text-lg md:text-xl text-white/65 font-light leading-relaxed mb-6">
            {scenario.benefit}
          </p>
          <p className="text-sm text-white/45 font-light italic">
            {scenario.hook}
          </p>

          {/* Product showcase */}
          {scenario.products && (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {scenario.products.map((p, pi) => (
                <motion.div
                  key={p.name}
                  initial={{ opacity: 0, y: 12 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{
                    duration: 0.5,
                    delay: 0.4 + pi * 0.08,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  className="group/prod rounded-xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-sm p-4 hover:bg-white/[0.08] hover:border-white/[0.15] transition-all duration-400"
                  data-testid={`product-${scenario.id}-${pi}`}
                >
                  <p
                    className="text-sm font-medium text-white/80 tracking-tight mb-0.5 group-hover/prod:text-white transition-colors duration-300"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {p.name}
                  </p>
                  <p className="text-xs text-white/45 font-light leading-snug group-hover/prod:text-white/60 transition-colors duration-300">
                    {p.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          )}

          <motion.div
            initial={{ width: 0 }}
            animate={inView ? { width: 64 } : {}}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="mt-8 h-px bg-white/10"
          />
        </motion.div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION 2.5 — VIDEO SHOWCASE (Samsung Ballie)
   Click-to-play YouTube embed — never leaves the page
   ═══════════════════════════════════════════════════════════════════════════ */
function VideoShowcase() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [playing, setPlaying] = useState(false);

  const VIDEO_ID = "v0XdLGYaNMw";
  const THUMB = `https://img.youtube.com/vi/${VIDEO_ID}/hqdefault.jpg`;

  return (
    <section
      ref={ref}
      className="py-20 md:py-28 bg-[#0A0A0A]"
      data-testid="smart-home-video-showcase"
    >
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-20 mb-10 md:mb-14">
          <div className="md:col-span-2">
            <motion.p
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.2 }}
              className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/40"
            >
              El futuro
            </motion.p>
          </div>
          <div className="md:col-span-10">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="text-2xl md:text-3xl lg:text-4xl font-normal text-white tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Esto ya es{" "}
              <span className="text-white/40">realidad</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="mt-3 text-base text-white/50 font-light max-w-lg"
            >
              Samsung Ballie — el asistente robótico con IA que cuida tu casa,
              tus mascotas y tu familia de forma autónoma.
            </motion.p>
          </div>
        </div>

        {/* Video container */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full rounded-2xl overflow-hidden border border-white/[0.06] aspect-video bg-black"
          data-testid="smart-home-video-player"
        >
          {playing ? (
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${VIDEO_ID}?autoplay=1&rel=0&modestbranding=1&showinfo=0&controls=1`}
              title="Samsung Ballie — Casa Inteligente"
              allow="autoplay; encrypted-media"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          ) : (
            <button
              onClick={() => {
                setPlaying(true);
                trackEvent("smart_home_video_play", {
                  section: "showcase",
                  video: "samsung_ballie",
                });
              }}
              className="absolute inset-0 w-full h-full group cursor-pointer"
              data-testid="smart-home-video-play-btn"
            >
              <img
                src={THUMB}
                alt="Samsung Ballie — Casa Inteligente"
                className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-500"
              />
              {/* Play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center group-hover:bg-white/20 group-hover:scale-110 transition-all duration-500">
                  <svg
                    viewBox="0 0 24 24"
                    fill="white"
                    className="w-8 h-8 md:w-10 md:h-10 ml-1"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
              {/* Bottom bar */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white/60 text-sm font-light">
                  Toca para reproducir
                </p>
              </div>
            </button>
          )}
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION 3 — MID-FUNNEL CTA
   ═══════════════════════════════════════════════════════════════════════════ */
function MidCTA({ onCTAClick }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      ref={ref}
      className="py-20 md:py-28 bg-white"
      data-testid="smart-home-mid-cta"
    >
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-neutral-400 mb-5">
            Estás a un paso
          </p>
          <h3
            className="text-3xl md:text-4xl lg:text-5xl font-normal text-neutral-900 tracking-tight max-w-2xl mx-auto leading-[1.1]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Tu hogar ideal{" "}
            <span className="text-neutral-300">ya existe</span>
          </h3>
          <p className="mt-5 text-base text-neutral-500 font-light max-w-lg mx-auto">
            Cuéntanos cómo imaginas tu vida y diseñamos la casa que la hace
            realidad.
          </p>
          <button
            onClick={() => {
              trackEvent("smart_home_cta_click", {
                location: "mid",
                type: "primary",
              });
              onCTAClick();
            }}
            className="mt-8 group inline-flex items-center gap-3 bg-black text-white px-8 py-4 rounded-full text-sm font-medium hover:bg-neutral-800 transition-all duration-500 active:scale-[0.97]"
            data-testid="smart-home-mid-cta-button"
          >
            Quiero mi propuesta
            <ArrowRight
              size={16}
              strokeWidth={2}
              className="group-hover:translate-x-1 transition-transform"
            />
          </button>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION 4 — TRUST & VALUE
   ═══════════════════════════════════════════════════════════════════════════ */
function TrustSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      ref={ref}
      className="py-24 md:py-32 bg-[#F5F5F7] relative noise-overlay"
      data-testid="smart-home-trust"
    >
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-20 mb-16">
          <div className="md:col-span-2">
            <motion.p
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.2 }}
              className="text-[11px] font-semibold uppercase tracking-[0.25em] text-neutral-400"
            >
              Beneficios
            </motion.p>
          </div>
          <div className="md:col-span-10">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="text-3xl md:text-4xl lg:text-5xl font-normal text-neutral-900 tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Lo que realmente{" "}
              <span className="text-neutral-400">ganas</span>
            </motion.h2>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TRUST_ITEMS.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 0.6,
                delay: 0.1 + i * 0.1,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="group bg-white rounded-2xl border border-neutral-100 p-8 hover:border-neutral-300 hover:shadow-[0_20px_60px_rgba(0,0,0,0.06)] transition-all duration-500"
              data-testid={`smart-home-trust-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-neutral-50 border border-neutral-100 flex items-center justify-center mb-6 group-hover:bg-black group-hover:border-black transition-all duration-500">
                <item.icon
                  size={18}
                  strokeWidth={1.5}
                  className="text-neutral-600 group-hover:text-white transition-colors duration-500"
                />
              </div>
              <p
                className="text-3xl md:text-4xl font-medium text-neutral-900 tracking-tight mb-1"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {item.stat}
              </p>
              <h4
                className="text-base font-medium text-neutral-900 tracking-tight mb-2"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {item.title}
              </h4>
              <p className="text-sm text-neutral-500 font-light leading-relaxed">
                {item.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION 5 — PRICING PACKAGES
   3 tiers with real pricing (product + installation + margin)
   ═══════════════════════════════════════════════════════════════════════════ */
const PLANS = [
  {
    id: "esencial",
    name: "Esencial",
    tagline: "Tu primer paso al hogar inteligente",
    target: "Ideal para departamentos o casas de 1-2 recámaras",
    price: "34,900",
    featured: false,
    items: [
      "Cerradura inteligente (August Smart Lock)",
      "4 focos inteligentes + hub (Philips Hue)",
      "2 sensores de movimiento (Aqara)",
      "Cámara Wi-Fi interior HD",
      "Asistente de voz (Amazon Echo Dot)",
      "Instalación profesional completa",
      "Configuración de app y escenas",
      "Garantía 1 año en dispositivos",
    ],
  },
  {
    id: "residencial",
    name: "Residencial",
    tagline: "La experiencia completa para tu familia",
    target: "Para casas de 3-4 recámaras",
    price: "89,900",
    featured: true,
    badge: "Más solicitado",
    items: [
      "Todo lo del plan Esencial, más:",
      "Termostato inteligente (Google Nest)",
      "Timbre con cámara (Ring Pro 2)",
      "2 cámaras exteriores HD (Arlo Pro)",
      "Alarma con 4 sensores de puertas",
      "Robot aspirador (Roborock S8)",
      "Bocina premium (Sonos Era 300)",
      "Cortinas motorizadas (1 habitación)",
      "Diseño de escenas personalizadas",
      "1 año soporte técnico telefónico",
    ],
  },
  {
    id: "signature",
    name: "Signature",
    tagline: "El máximo nivel de vida inteligente",
    target: "Residencias de lujo de 5+ recámaras",
    price: "159,900",
    featured: false,
    items: [
      "Todo lo del plan Residencial, más:",
      "Audio multiroom 3 zonas (Sonos Era 300)",
      "Cortinas motorizadas en toda la casa",
      "4 cámaras con IA + grabador NVR",
      "Cerradura biométrica premium",
      "Kit mascotas (Furbo + comedero + GPS)",
      "Electrodomésticos conectados",
      "Diseño de automatización a medida",
      "Instalación premium con ingeniería",
      "Soporte prioritario 1 año",
      "Mantenimiento preventivo trimestral",
    ],
  },
];

function PricingSection({ onPlanClick }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      ref={ref}
      className="py-24 md:py-36 bg-[#0A0A0A] relative overflow-hidden"
      data-testid="smart-home-pricing"
    >
      {/* Subtle glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-white/[0.01] rounded-full blur-[120px] pointer-events-none" />

      <div className="relative z-10 max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-16 md:mb-20">
          <motion.p
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.1 }}
            className="text-[11px] font-semibold uppercase tracking-[0.3em] text-white/40 mb-5"
          >
            Paquetes con instalación
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="text-3xl md:text-4xl lg:text-5xl font-medium text-white tracking-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Elige tu nivel de{" "}
            <span className="text-white/30">inteligencia</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 text-base text-white/45 font-light max-w-lg mx-auto"
          >
            Todos los paquetes incluyen dispositivos, instalación profesional,
            configuración y garantía. Precio final, sin sorpresas.
          </motion.p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-3 lg:gap-4">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 0.7,
                delay: 0.15 + i * 0.1,
                ease: [0.16, 1, 0.3, 1],
              }}
              className={`relative rounded-2xl p-7 md:p-8 flex flex-col transition-all duration-500 ${
                plan.featured
                  ? "bg-white text-black border-2 border-white md:scale-[1.03] shadow-[0_30px_80px_rgba(255,255,255,0.08)]"
                  : "bg-white/[0.03] border border-white/[0.08] hover:bg-white/[0.05] hover:border-white/[0.15]"
              }`}
              data-testid={`smart-home-plan-${plan.id}`}
            >
              {/* Badge */}
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1.5 bg-black text-white text-[10px] font-semibold uppercase tracking-[0.15em] px-4 py-1.5 rounded-full">
                    <Star size={10} fill="currentColor" />
                    {plan.badge}
                  </span>
                </div>
              )}

              {/* Plan header */}
              <div className="mb-6">
                <h3
                  className={`text-lg font-medium tracking-tight mb-1 ${
                    plan.featured ? "text-black" : "text-white"
                  }`}
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {plan.name}
                </h3>
                <p
                  className={`text-xs font-light ${
                    plan.featured ? "text-neutral-500" : "text-white/50"
                  }`}
                >
                  {plan.tagline}
                </p>
              </div>

              {/* Price */}
              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span
                    className={`text-sm font-light ${
                      plan.featured ? "text-neutral-400" : "text-white/25"
                    }`}
                  >
                    $
                  </span>
                  <span
                    className={`text-4xl md:text-5xl font-medium tracking-tight ${
                      plan.featured ? "text-black" : "text-white"
                    }`}
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {plan.price}
                  </span>
                  <span
                    className={`text-xs font-light ml-1 ${
                      plan.featured ? "text-neutral-400" : "text-white/35"
                    }`}
                  >
                    MXN
                  </span>
                </div>
                <p
                  className={`text-[11px] mt-2 font-light ${
                    plan.featured ? "text-neutral-500" : "text-white/35"
                  }`}
                >
                  {plan.target}
                </p>
              </div>

              {/* Divider */}
              <div
                className={`h-px mb-6 ${
                  plan.featured ? "bg-neutral-200" : "bg-white/[0.06]"
                }`}
              />

              {/* Items */}
              <ul className="space-y-3 flex-1 mb-8">
                {plan.items.map((item, ii) => (
                  <li key={ii} className="flex gap-2.5">
                    <Check
                      size={14}
                      strokeWidth={2}
                      className={`mt-0.5 flex-shrink-0 ${
                        plan.featured ? "text-black" : "text-white/30"
                      }`}
                    />
                    <span
                      className={`text-xs font-light leading-relaxed ${
                        plan.featured ? "text-neutral-600" : "text-white/55"
                      }`}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={() => {
                  trackEvent("smart_home_cta_click", {
                    location: "pricing",
                    plan: plan.id,
                    price: plan.price,
                  });
                  onPlanClick(plan);
                }}
                className={`w-full group inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-sm font-medium transition-all duration-500 active:scale-[0.97] ${
                  plan.featured
                    ? "bg-black text-white hover:bg-neutral-800"
                    : "bg-white/[0.06] text-white/70 border border-white/[0.1] hover:bg-white/[0.1] hover:text-white"
                }`}
                data-testid={`smart-home-plan-cta-${plan.id}`}
              >
                Cotizar paquete {plan.name}
                <ArrowRight
                  size={14}
                  strokeWidth={2}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
          className="mt-10 text-center text-xs text-white/30 font-light max-w-lg mx-auto"
        >
          * Precios incluyen IVA, dispositivos, instalación profesional y
          configuración. Paquetes personalizables según las necesidades de tu
          hogar. Financiamiento disponible.
        </motion.p>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION 6 — FINAL CTA
   ═══════════════════════════════════════════════════════════════════════════ */
function FinalCTA({ onCTAClick }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      ref={ref}
      className="relative py-28 md:py-40 bg-[#0A0A0A] overflow-hidden"
      data-testid="smart-home-final-cta"
    >
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[500px] h-[500px] bg-white/[0.015] rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium text-white tracking-tight leading-[1.05] max-w-4xl mx-auto"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            La vida que imaginas está a{" "}
            <span className="text-white/25">una conversación.</span>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-white/55 font-light max-w-xl mx-auto leading-relaxed">
            Sin compromiso. Sin costo. Solo posibilidades.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => {
                trackEvent("smart_home_cta_click", {
                  location: "final",
                  type: "whatsapp",
                });
                onCTAClick();
              }}
              className="group inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full text-sm font-medium hover:shadow-[0_10px_50px_rgba(255,255,255,0.2)] transition-all duration-500 active:scale-[0.97]"
              data-testid="smart-home-final-cta-whatsapp"
            >
              Agendar mi consultoría
              <ArrowRight
                size={16}
                strokeWidth={2}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
            <button
              onClick={() => {
                trackEvent("smart_home_cta_click", {
                  location: "final",
                  type: "form",
                });
                document
                  .querySelector("#contacto")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center gap-2 text-white/45 hover:text-white/70 text-sm font-light transition-colors duration-300 px-6 py-4"
              data-testid="smart-home-final-cta-contact"
            >
              Prefiero escribirles
            </button>
          </div>

          <p className="mt-8 text-xs text-white/25 font-light">
            Te respondemos en menos de 24 horas
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN
   ═══════════════════════════════════════════════════════════════════════════ */
export default function SmartHomesSection() {
  const [whatsapp, setWhatsapp] = useState("5544421964");

  useEffect(() => {
    api
      .getSettings()
      .then((r) => {
        if (r.data?.whatsapp) setWhatsapp(r.data.whatsapp);
      })
      .catch(() => {});
  }, []);

  const handleCTA = useCallback(() => {
    api
      .createLead({
        name: "Interesado Casa Inteligente",
        email: "pendiente@via-whatsapp.com",
        phone: "",
        message: "Interés en Casas Inteligentes — clic CTA funnel",
        service: "Casas Inteligentes",
        source: "smart_homes",
      })
      .catch(() => {});

    const msg = encodeURIComponent(
      "Hola, me interesa una *Casa Inteligente* con ZYM Arquitectos. ¿Podemos agendar una consultoría?"
    );
    window.open(`https://wa.me/${whatsapp}?text=${msg}`, "_blank");
    trackEvent("smart_home_whatsapp_click", { phone: whatsapp });
  }, [whatsapp]);

  const handlePlanCTA = useCallback(
    (plan) => {
      api
        .createLead({
          name: `Interesado Paquete ${plan.name}`,
          email: "pendiente@via-whatsapp.com",
          phone: "",
          message: `Interés en Paquete ${plan.name} ($${plan.price} MXN) — Casas Inteligentes`,
          service: "Casas Inteligentes",
          source: "smart_homes",
        })
        .catch(() => {});

      const msg = encodeURIComponent(
        `Hola, me interesa el *Paquete ${plan.name}* ($${plan.price} MXN) de Casas Inteligentes con ZYM Arquitectos. ¿Podemos agendar una consultoría?`
      );
      window.open(`https://wa.me/${whatsapp}?text=${msg}`, "_blank");
      trackEvent("smart_home_whatsapp_click", {
        phone: whatsapp,
        plan: plan.id,
      });
    },
    [whatsapp]
  );

  return (
    <section id="casas-inteligentes" data-testid="smart-homes-section">
      <FunnelHero onCTAClick={handleCTA} />
      <div id="smart-home-stories">
        {SCENARIOS.map((s, i) => (
          <ScenarioCard key={s.id} scenario={s} index={i} />
        ))}
      </div>
      <VideoShowcase />
      <MidCTA onCTAClick={handleCTA} />
      <TrustSection />
      <PricingSection onPlanClick={handlePlanCTA} />
      <FinalCTA onCTAClick={handleCTA} />
    </section>
  );
}
